import g from '#core/global';
import load from '#core/load';


const state = window.teal_state = window.teal_state || {};
state.json_viewer = state.json_viewer || {};
state.json_viewer.expansion_states = state.json_viewer.expansion_states || new Map();
state.json_viewer.search_states = state.json_viewer.search_states || new Map();

const item_c = 'json-viewer__item';
const item_clickable_c = 'json-viewer__item--clickable';
const key_c = 'json-viewer__key';
const value_c = 'json-viewer__value';
const count_c = 'json-viewer__count';
const preview_c = 'json-viewer__preview';
const preview_text_c = 'json-viewer__preview__text';
const children_c = 'json-viewer__children';
const toggle_icon_c = 'json-viewer__toggle';
const value_type_string_c = 'json-viewer__string';
const value_type_number_c = 'json-viewer__number';
const value_type_boolean_c = 'json-viewer__boolean';
const value_type_null_c = 'json-viewer__null';

// Search-related constants
const toolbar_c = 'json-viewer__toolbar';
const search_c = 'json-viewer__search';
const search_toggle_c = 'json-viewer__search-toggle';
const search_expanded_c = 'json-viewer__search-expanded';
const search_input_c = 'json-viewer__search-input';
const search_modes_c = 'json-viewer__search-modes';
const search_mode_c = 'json-viewer__search-mode';
const search_clear_c = 'json-viewer__search-clear';
const search_results_c = 'json-viewer__search-results';
const search_count_c = 'json-viewer__search-count';
const match_highlight_c = 'json-viewer__match-highlight';
const match_highlight_key_c = 'json-viewer__match-highlight--key';
const match_highlight_value_c = 'json-viewer__match-highlight--value';

const search_active_class = 'is-search-active';
const match_class = 'is-match';
const match_key_class = 'is-match-key';
const match_value_class = 'is-match-value';
const match_ancestor_class = 'is-match-ancestor';
const hidden_by_search_class = 'is-hidden-by-search';

const json_viewer_$ = '.json-viewer';
const source_data_$ = '.json-viewer__source__data';
const item_$ = `.${item_c}`;
const search_$ = `.${search_c}`;
const search_toggle_$ = `.${search_toggle_c}`;
const search_input_$ = `.${search_input_c}`;
const search_mode_$ = `.${search_mode_c}`;
const search_clear_$ = `.${search_clear_c}`;
const expanded_class = 'is-expanded';
const collapsed_class = 'is-collapsed';
const inline_class = 'has-inline-value';

const path_attr = 'data-path';
const path_root_key = 'root';
const value_type_attr = 'data-type';
const viewer_id_attr = 'data-viewer-id';
const label_attr = 'data-label';
const depth_attr = 'data-max-depth';
const json_data_attr = 'data-json';

const default_max_depth = 100;
const string_truncate_length = 200;
const search_debounce_ms = 200;
const search_min_chars = 3;

const toggle_icon_collapsed = '<svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M4 2L8 6L4 10" stroke="currentColor" stroke-width="2" vector-effect="non-scaling-stroke"/></svg>';
const search_icon_svg = '<svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg"><circle cx="7" cy="7" r="5" stroke="currentColor" stroke-width="2" vector-effect="non-scaling-stroke"/><path d="M11 11L14 14" stroke="currentColor" stroke-width="2" vector-effect="non-scaling-stroke"/></svg>';
const close_icon_svg = '<svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M4 4L12 12M12 4L4 12" stroke="currentColor" stroke-width="2" vector-effect="non-scaling-stroke"/></svg>';


const module = {
	
	/**
	 * Init module
	 */
	init() {
		if (load.ran('json-viewer')) return false;
		
		load.content(() => {
			assign_elements();
		});
	},
	
};

export default module;
module.init();


/**
 * Assign elements and initialize viewers
 */
function assign_elements() {
	const $viewers = document.querySelectorAll(json_viewer_$);
	
	$viewers.forEach($viewer => {
		if ($viewer.hasAttribute(viewer_id_attr)) return;
		
		// Get JSON data from nested source element
		const $source_data = $viewer.querySelector(source_data_$);
		if (!$source_data) return;
		
		const json_data = $source_data.getAttribute(json_data_attr);
		if (!json_data) return;
		
		// Parse JSON
		let data;
		try {
			data = JSON.parse(json_data);
		} catch (e) {
			$viewer.innerHTML = '<div class="json-viewer__error">Invalid JSON data</div>';
			return;
		}
		
		// Get max depth
		const max_depth_attr = $viewer.getAttribute(depth_attr);
		const max_depth = max_depth_attr
			? parseInt(max_depth_attr, 10)
			: default_max_depth;
		
		// Get custom label for root key
		const label = $viewer.getAttribute(label_attr);
		
		// Generate and store viewer ID
		const viewer_id = generate_viewer_id();
		$viewer.setAttribute(viewer_id_attr, viewer_id);
		
		// Render toolbar BEFORE JSON content
		const toolbar_html = render_toolbar(viewer_id);
		$viewer.insertAdjacentHTML('beforeend', toolbar_html);
		
		// Render JSON and append after toolbar
		const html = render_json(data, [path_root_key], 0, max_depth, viewer_id, label);
		$viewer.insertAdjacentHTML('beforeend', html);
	});
	
	// Initialize search listeners (once)
	if (!state.json_viewer.search_listeners_initialized) {
		initialize_search_listeners();
		state.json_viewer.search_listeners_initialized = true;
	}
	
	document.addEventListener(g.click_e, e => {
		const $clickable_item = e.target.closest(`.${item_clickable_c}`);
		if (!$clickable_item) return;
		
		e.preventDefault();
		const is_option_click = e.altKey || e.metaKey || e.ctrlKey;
		toggle_item($clickable_item, is_option_click);
	});
}


/**
 * Generate unique viewer instance ID
 * @return {string}
 */
function generate_viewer_id() {
	return 'viewer-' + Math.random().toString(36).substring(2);
}


/**
 * Generate path for an item based on its location in the data structure
 * @param {Array} path_array
 * @return {string}
 */
function generate_item_path(path_array) {
	return path_array.join('.');
}


/**
 * Generate full key for state Map (combines instance ID + path)
 * @param {string} viewer_id
 * @param {string} path
 * @return {string}
 */
function generate_state_key(viewer_id, path) {
	return viewer_id + '.' + path;
}


/**
 * Get viewer ID from an element by traversing up to find .json-viewer
 * @param {HTMLElement} $element
 * @return {string}
 */
function get_viewer_id($element) {
	const $viewer = $element.closest(json_viewer_$);
	return $viewer
		? $viewer.getAttribute(viewer_id_attr)
		: '';
}


/**
 * Check if value is scalar (primitive)
 * @param {*} value
 * @return {boolean}
 */
function is_scalar(value) {
	return (
		typeof value === 'string'
		|| typeof value === 'number'
		|| typeof value === 'boolean'
		|| value === null
	);
}


/**
 * Format a key for display (quotes for strings, no quotes for numbers)
 * @param {string|number} key
 * @return {string}
 */
function format_key(key) {
	// If key is numeric (or string that looks like a number), don't quote it
	if (typeof key === 'number' || !isNaN(key)) {
		return String(key);
	}
	
	return `"${key}"`;
}


/**
 * Truncate string if it exceeds max length
 * @param {string} str
 * @param {number} max_length
 * @return {string}
 */
function truncate_string(str, max_length = string_truncate_length) {
	if (str.length <= max_length) return str;
	
	return str.substring(0, max_length) + '... (' + str.length + ' chars)';
}


/**
 * Render depth limit message
 * @return {string}
 */
function render_depth_limit_message() {
	return '<span class="json-viewer__depth-limit">Maximum depth reached</span>';
}


/**
 * Get count display for arrays/objects
 * @param {*} data
 * @param {string} type
 * @return {string}
 */
function get_count_display(data, type) {
	if (type === 'array') {
		const count = data.length;
		const label = count === 1 ? 'item' : 'items';
		return ` (${count} ${label})`;
	}
	
	if (type === 'object') {
		const count = Object.keys(data).length;
		const label = count === 1 ? 'key' : 'keys';
		return ` (${count} ${label})`;
	}
	
	return '';
}


/**
 * Determine if data should display inline
 * @param {*} data
 * @param {string} type
 * @return {Object}
 */
function should_display_inline(data, type) {
	if (type === 'array') {
		if (data.length !== 1) return { is_inline: false };
		const item = data[0];
		if (is_scalar(item)) {
			return { is_inline: true, display_value: item };
		}
		
		return { is_inline: false };
	}
	
	if (type === 'object') {
		const keys = Object.keys(data);
		if (keys.length !== 1) return { is_inline: false, display_key: null };
		
		const key = keys[0];
		const value = data[key];
		
		if (is_scalar(value)) {
			return { is_inline: true, display_key: key, display_value: value };
		}
		
		// Single key with non-scalar value: show key in preview but keep collapsible
		return { is_inline: false, display_key: key };
	}
	
	return { is_inline: false, display_key: null };
}


/**
 * Create toggle icon HTML
 * @param {string} path
 * @return {string}
 */
function create_toggle_icon(path) {
	return `<span class="${toggle_icon_c}" ${path_attr}="${path}">${toggle_icon_collapsed}</span>`;
}


/**
 * Render scalar value
 * @param {*} value
 * @return {string}
 */
function render_scalar(value) {
	if (value === null) {
		return `<span class="${value_type_null_c}">null</span>`;
	}
	
	if (typeof value === 'boolean') {
		return `<span class="${value_type_boolean_c}">${value}</span>`;
	}
	
	if (typeof value === 'number') {
		return `<span class="${value_type_number_c}">${value}</span>`;
	}
	
	if (typeof value === 'string') {
		const truncated = truncate_string(value);
		const escaped = truncated
			.replace(/&/g, '&amp;')
			.replace(/</g, '&lt;')
			.replace(/>/g, '&gt;')
			.replace(/"/g, '&quot;')
		;
		
		return `<span class="${value_type_string_c}">"${escaped}"</span>`;
	}
	
	return '';
}


/**
 * Render array
 * @param {Array} data
 * @param {Array} path
 * @param {number} current_depth
 * @param {number} max_depth
 * @param {string} viewer_id
 * @param {string} display_label
 * @return {string}
 */
function render_array(data, path, current_depth, max_depth, viewer_id, display_label = null) {
	const path_string = generate_item_path(path);
	const count_display = get_count_display(data, 'array');
	const inline_check = should_display_inline(data, 'array');
	const is_empty = data.length === 0;
	
	// Use display_label at root level, otherwise use path key
	const key_to_display = (current_depth === 0 && display_label)
		? display_label
		: path[path.length - 1];
	
	if (inline_check.is_inline) {
		const value_html = render_scalar(inline_check.display_value);
		const key_display = path.length > 0
			? `${format_key(key_to_display)}: `
			: '';
		
		return `<div class="${item_c} ${inline_class}" ${path_attr}="${path_string}" ${value_type_attr}="array">
			<span class="${key_c}">${key_display}</span>
			<span class="${value_c}">[ ${value_html} ]</span>
			<span class="${count_c}">${count_display}</span>
		</div>`;
	}
	
	// Hide toggle for empty arrays
	const toggle_html = !is_empty
		? create_toggle_icon(path_string)
		: '';
	const key_display = path.length > 0
		? `${format_key(key_to_display)}: `
		: '';
	const clickable_class = !is_empty
		? item_clickable_c
		: '';
	
	let html = `<div class="${item_c} ${clickable_class} ${collapsed_class}" ${path_attr}="${path_string}" ${value_type_attr}="array">
		${toggle_html}
		<span class="${key_c}">${key_display}</span>
		<span class="${preview_c}">[]</span>
		<span class="${count_c}">${count_display}</span>
	</div>`;
	
	html += `<div class="${children_c}">`;
	
	data.forEach((item, index) => {
		const item_path = [...path, index];
		html += render_json(item, item_path, current_depth + 1, max_depth, viewer_id, null);
	});
	
	html += '</div>';
	
	return html;
}


/**
 * Render object
 * @param {Object} obj
 * @param {Array} path
 * @param {number} current_depth
 * @param {number} max_depth
 * @param {string} viewer_id
 * @param {string} display_label
 * @return {string}
 */
function render_object(obj, path, current_depth, max_depth, viewer_id, display_label = null) {
	const path_str = generate_item_path(path);
	const count_display = get_count_display(obj, 'object');
	const inline_check = should_display_inline(obj, 'object');
	
	// Use display_label at root level, otherwise use path key
	const key_to_display = (current_depth === 0 && display_label)
		? display_label
		: path[path.length - 1];
	
	if (inline_check.is_inline) {
		const value_html = render_scalar(inline_check.display_value);
		const parent_key = path.length > 0
			? `${format_key(key_to_display)}: `
			: '';
		
		return `<div class="${item_c} ${inline_class}" ${path_attr}="${path_str}" ${value_type_attr}="object">
			<span class="${key_c}">${parent_key}</span>
			<span class="${value_c}">{ "${inline_check.display_key}": ${value_html} }</span>
			<span class="${count_c}">${count_display}</span>
		</div>`;
	}
	
	const toggle_html = create_toggle_icon(path_str);
	const key_display = path.length > 0
		? `${format_key(key_to_display)}: `
		: '';
	
	// Show key name in preview if single key with non-scalar value
	const preview_display = inline_check.display_key
		? `{<span class="${preview_text_c}"> "${inline_check.display_key}": {} </span>}`
		: '{}';
	
	let html = `<div class="${item_c} ${item_clickable_c} ${collapsed_class}" ${path_attr}="${path_str}" ${value_type_attr}="object">
		${toggle_html}
		<span class="${key_c}">${key_display}</span>
		<span class="${preview_c}">${preview_display}</span>
		<span class="${count_c}">${count_display}</span>
	</div>`;
	
	html += `<div class="${children_c}">`;
	
	Object.keys(obj).forEach(key => {
		const item_path = [...path, key];
		html += render_json(obj[key], item_path, current_depth + 1, max_depth, viewer_id, null);
	});
	
	html += '</div>';
	return html;
}


/**
 * Render JSON data recursively
 * @param {*} data
 * @param {Array} path
 * @param {number} current_depth
 * @param {number} max_depth
 * @param {string} viewer_id
 * @param {string} display_label
 * @return {string}
 */
function render_json(data, path = [], current_depth = 0, max_depth = default_max_depth, viewer_id = '', display_label = null) {
	// Check depth limit
	if (max_depth !== -1 && current_depth >= max_depth) {
		return render_depth_limit_message();
	}
	
	// Handle arrays
	if (Array.isArray(data)) {
		return render_array(data, path, current_depth, max_depth, viewer_id, display_label);
	}
	
	// Handle objects
	if (typeof data === 'object' && data !== null) {
		return render_object(data, path, current_depth, max_depth, viewer_id, display_label);
	}
	
	// Handle scalar values
	const path_str = generate_item_path(path);
	// Use display_label at root level, otherwise use path key
	const key_to_display = (current_depth === 0 && display_label)
		? display_label
		: path[path.length - 1];
	const key_display = path.length > 0
		? `${format_key(key_to_display)}: `
		: '';
	const value_html = render_scalar(data);
	
	return `<div class="${item_c}" ${path_attr}="${path_str}" ${value_type_attr}="scalar">
		<span class="${key_c}">${key_display}</span>
		${value_html}
	</div>`;
}


/**
 * Expand an item
 * @param {HTMLElement} $item
 * @param {string} viewer_id
 * @param {boolean} recursive
 */
function expand_item($item, viewer_id, recursive = false) {
	const path = $item.getAttribute(path_attr);
	const state_key = generate_state_key(viewer_id, path);
	
	// Update state
	state.json_viewer.expansion_states.set(state_key, true);
	
	// Update DOM
	$item.classList.remove(collapsed_class);
	$item.classList.add(expanded_class);
	
	// Recursive expansion
	if (recursive) {
		const $children = $item.nextElementSibling;
		if ($children && $children.classList.contains(children_c)) {
			const $descendant_items = $children.querySelectorAll(item_$);
			$descendant_items.forEach($descendant => {
				if (!$descendant.classList.contains(inline_class)) {
					expand_item($descendant, viewer_id, false);
				}
			});
		}
	}
}


/**
 * Collapse an item
 * @param {HTMLElement} $item
 * @param {string} viewer_id
 * @param {boolean} recursive
 */
function collapse_item($item, viewer_id, recursive = false) {
	const path = $item.getAttribute(path_attr);
	const state_key = generate_state_key(viewer_id, path);
	
	// Update state
	state.json_viewer.expansion_states.set(state_key, false);
	
	// Update DOM
	$item.classList.remove(expanded_class);
	$item.classList.add(collapsed_class);
	
	// Recursive collapse
	if (recursive) {
		const $children = $item.nextElementSibling;
		if ($children && $children.classList.contains(children_c)) {
			const $descendant_items = $children.querySelectorAll(item_$);
			$descendant_items.forEach($descendant => {
				if (!$descendant.classList.contains(inline_class)) {
					collapse_item($descendant, viewer_id, false);
				}
			});
		}
	}
}


/**
 * Toggle item expansion state
 * @param {HTMLElement} $item
 * @param {boolean} is_option_click
 */
function toggle_item($item, is_option_click) {
	if (!$item) return;
	
	const viewer_id = get_viewer_id($item);
	const path = $item.getAttribute(path_attr);
	const state_key = generate_state_key(viewer_id, path);
	
	// Get current state (defaults to false/collapsed if not in Map)
	const is_expanded = state.json_viewer.expansion_states.get(state_key) || false;
	
	// Toggle
	if (is_expanded) {
		collapse_item($item, viewer_id, is_option_click);
	} else {
		expand_item($item, viewer_id, is_option_click);
	}
}


/**
 * Get or create search state for a viewer
 * @param {string} viewer_id
 * @return {Object}
 */
function get_search_state(viewer_id) {
	if (!state.json_viewer.search_states.has(viewer_id)) {
		state.json_viewer.search_states.set(viewer_id, {
			query: '',
			match_count: 0,
			debounce_timer: null
		});
	}
	
	return state.json_viewer.search_states.get(viewer_id);
}


/**
 * Render toolbar HTML for search
 * @param {string} viewer_id
 * @return {string}
 */
function render_toolbar(viewer_id) {
	return `
		<div class="${toolbar_c}">
			<div class="${search_results_c}" hidden>
				<span class="${search_count_c}">0 matches</span>
			</div>
			<div class="${search_c}">
				<input
					type="text"
					name="json-viewer-search"
					class="${search_input_c}"
					placeholder="Search"
					aria-label="Search JSON data"
				>
				<button class="${search_toggle_c}" aria-label="Search or clear">
					${search_icon_svg}
				</button>
			</div>
		</div>
	`;
}


/**
 * Initialize search event listeners
 */
function initialize_search_listeners() {
	// Toggle button: clear search if active, otherwise do nothing (field is always visible)
	document.addEventListener(g.click_e, e => {
		const $toggle = e.target.closest(search_toggle_$);
		if (!$toggle) return;
		e.preventDefault();
		
		const $viewer = $toggle.closest(json_viewer_$);
		const viewer_id = $viewer.getAttribute(viewer_id_attr);
		const search_state = get_search_state(viewer_id);
		
		// If there's a query, clear it
		if (search_state.query) {
			const $input = $viewer.querySelector(`.${search_input_c}`);
			clear_search($input);
		}
	});
	
	// Search input
	document.addEventListener('input', e => {
		const $input = e.target.closest(search_input_$);
		if (!$input) return;
		handle_search_input($input);
	});
	
	// ESC to clear search
	document.addEventListener('keydown', e => {
		if (e.key === 'Escape') {
			const $input = document.activeElement;
			if ($input && $input.classList.contains(search_input_c)) {
				clear_search($input);
			}
		}
	});
}


/**
 * Handle search input
 * @param {HTMLElement} $input
 */
function handle_search_input($input) {
	const $viewer = $input.closest(json_viewer_$);
	const viewer_id = $viewer.getAttribute(viewer_id_attr);
	const search_state = get_search_state(viewer_id);
	
	// Clear existing debounce timer
	if (search_state.debounce_timer) {
		clearTimeout(search_state.debounce_timer);
	}
	
	// Update query
	search_state.query = $input.value.trim().toLowerCase();
	
	// Update icon immediately based on whether there's a query
	update_search_icon($viewer, search_state.query.length > 0);
	
	// Debounce search execution
	search_state.debounce_timer = setTimeout(() => {
		perform_search($viewer, viewer_id);
	}, search_debounce_ms);
}


/**
 * Update search icon (search or X)
 * @param {HTMLElement} $viewer
 * @param {boolean} has_query - true shows X icon, false shows search icon
 */
function update_search_icon($viewer, has_query) {
	const $toggle = $viewer.querySelector(`.${search_toggle_c}`);
	if (!$toggle) return;
	
	$toggle.innerHTML = has_query ? close_icon_svg : search_icon_svg;
	$toggle.setAttribute('aria-label', has_query ? 'Clear search' : 'Search JSON');
}


/**
 * Clear search
 * @param {HTMLElement} $clear_or_input
 */
function clear_search($clear_or_input) {
	const $viewer = $clear_or_input.closest(json_viewer_$);
	const viewer_id = $viewer.getAttribute(viewer_id_attr);
	const search_state = get_search_state(viewer_id);
	
	// Clear input
	const $input = $viewer.querySelector(`.${search_input_c}`);
	$input.value = '';
	
	// Clear state
	search_state.query = '';
	search_state.match_count = 0;
	
	// Update icon to search icon
	update_search_icon($viewer, false);
	
	// Clear highlights
	clear_search_highlights($viewer);
	
	// Hide results count
	const $results = $viewer.querySelector(`.${search_results_c}`);
	if ($results) {
		$results.hidden = true;
	}
	
	$viewer.classList.remove(search_active_class);
}


/**
 * Perform search
 * @param {HTMLElement} $viewer
 * @param {string} viewer_id
 */
function perform_search($viewer, viewer_id) {
	const search_state = get_search_state(viewer_id);
	const query = search_state.query;
	
	// Clear previous search state
	clear_search_highlights($viewer);
	
	// If query is too short, early return
	if (query.length < search_min_chars) {
		$viewer.classList.remove(search_active_class);
		const $results = $viewer.querySelector(`.${search_results_c}`);
		if ($results) {
			$results.hidden = true;
		}
		return;
	}
	
	$viewer.classList.add(search_active_class);
	
	// Find all items in viewer (excluding toolbar)
	const $items = $viewer.querySelectorAll(item_$);
	const matches = [];
	
	// Find the root item (direct child of viewer)
	const $root_item = $viewer.querySelector(`:scope > ${item_$}`);
	
	$items.forEach($item => {
		const match_result = check_item_match($item, query);
		if (match_result.is_match) {
			matches.push({ $item, ...match_result });
			apply_match_highlight($item, match_result);
		} else {
			// Don't hide the root item, even if it doesn't match
			if ($item !== $root_item) {
				$item.classList.add(hidden_by_search_class);
			}
		}
	});
	
	// If no matches found, mark root as ancestor so it stays visible
	if (matches.length === 0 && $root_item) {
		$root_item.classList.add(match_ancestor_class);
	}
	
	// Expand ancestors of all matches
	matches.forEach(match => {
		expand_ancestors_to_match(match.$item, viewer_id);
	});
	
	// Update count
	update_search_count($viewer, matches.length);
	
	// Store match count in state
	search_state.match_count = matches.length;
}


/**
 * Check if item matches search query
 * @param {HTMLElement} $item
 * @param {string} query
 * @return {Object}
 */
function check_item_match($item, query) {
	const type = $item.getAttribute(value_type_attr);
	const path = $item.getAttribute(path_attr);
	
	// Extract key (last segment of path)
	const path_segments = path.split('.');
	const key = path_segments[path_segments.length - 1];
	
	let key_matches = [];
	let value_matches = [];
	
	// Check key matches (find all occurrences)
	const key_lower = key.toLowerCase();
	let key_index = 0;
	while ((key_index = key_lower.indexOf(query, key_index)) !== -1) {
		key_matches.push([key_index, key_index + query.length]);
		key_index += query.length;
	}
	
	// Check value matches (find all occurrences)
	const $value_element = $item.querySelector(`.${value_type_string_c}, .${value_type_number_c}, .${value_type_boolean_c}`);
	if ($value_element) {
		const value_text = $value_element.textContent.toLowerCase();
		let value_index = 0;
		while ((value_index = value_text.indexOf(query, value_index)) !== -1) {
			value_matches.push([value_index, value_index + query.length]);
			value_index += query.length;
		}
	}
	
	const is_match = key_matches.length > 0 || value_matches.length > 0;
	
	return {
		is_match,
		has_key_match: key_matches.length > 0,
		has_value_match: value_matches.length > 0,
		match_indices_key: key_matches,
		match_indices_value: value_matches
	};
}


/**
 * Apply match highlight to item
 * @param {HTMLElement} $item
 * @param {Object} match_result
 */
function apply_match_highlight($item, match_result) {
	$item.classList.add(match_class);
	
	// Highlight key if it matches
	if (match_result.has_key_match) {
		$item.classList.add(match_key_class);
		
		const $key = $item.querySelector(`.${key_c}`);
		if ($key && match_result.match_indices_key.length > 0) {
			const original_text = $key.textContent;
			
			// Find the actual key within the text (skip quotes if present)
			// Key display format is either `"key": ` or `key: ` or `"key"` or `key`
			let key_start = 0;
			
			// Check if key starts with quote
			if (original_text.startsWith('"')) {
				key_start = 1;
			}
			
			// Build highlighted string by inserting marks around all matches
			let highlighted = '';
			let last_index = 0;
			
			match_result.match_indices_key.forEach(([match_start, match_end]) => {
				const adjusted_start = key_start + match_start;
				const adjusted_end = key_start + match_end;
				
				highlighted += original_text.slice(last_index, adjusted_start);
				highlighted += '<mark class="' + match_highlight_c + ' ' + match_highlight_key_c + '">';
				highlighted += original_text.slice(adjusted_start, adjusted_end);
				highlighted += '</mark>';
				last_index = adjusted_end;
			});
			
			highlighted += original_text.slice(last_index);
			$key.innerHTML = highlighted;
		}
	}
	
	// Highlight value if it matches
	if (match_result.has_value_match) {
		$item.classList.add(match_value_class);
		
		const $value = $item.querySelector(`.${value_type_string_c}, .${value_type_number_c}, .${value_type_boolean_c}`);
		if ($value && match_result.match_indices_value.length > 0) {
			// For string values, the innerHTML contains escaped HTML entities
			// We need to preserve the escaping
			const original_text = $value.textContent;
			
			// Build highlighted string by inserting marks around all matches
			let highlighted = '';
			let last_index = 0;
			
			match_result.match_indices_value.forEach(([match_start, match_end]) => {
				highlighted += original_text.slice(last_index, match_start);
				highlighted += '<mark class="' + match_highlight_c + ' ' + match_highlight_value_c + '">';
				highlighted += original_text.slice(match_start, match_end);
				highlighted += '</mark>';
				last_index = match_end;
			});
			
			highlighted += original_text.slice(last_index);
			
			// Re-escape for HTML
			const escaped_highlighted = highlighted
				.replace(/&/g, '&amp;')
				.replace(/</g, '&lt;')
				.replace(/>/g, '&gt;')
				.replace(/"/g, '&quot;')
				// But restore the mark tags
				.replace(/&lt;mark class=&quot;([^&]+)&quot;&gt;/g, '<mark class="$1">')
				.replace(/&lt;\/mark&gt;/g, '</mark>');
			
			$value.innerHTML = escaped_highlighted;
		}
	}
}


/**
 * Expand ancestors to match
 * @param {HTMLElement} $item
 * @param {string} viewer_id
 */
function expand_ancestors_to_match($item, viewer_id) {
	let $current = $item;
	
	while ($current) {
		// Find parent item by traversing up through .json-viewer__children containers
		const $children_container = $current.parentElement;
		if (
			!$children_container
			|| !$children_container.classList.contains(children_c)
		) {
			break;
		}
		
		const $parent_item = $children_container.previousElementSibling;
		if (!$parent_item || !$parent_item.classList.contains(item_c)) {
			break;
		}
		
		// Mark as ancestor and expand
		$parent_item.classList.add(match_ancestor_class);
		$parent_item.classList.remove(hidden_by_search_class);
		
		// Expand if collapsed
		if ($parent_item.classList.contains(collapsed_class)) {
			expand_item($parent_item, viewer_id, false);
		}
		
		$current = $parent_item;
	}
}


/**
 * Clear search highlights
 * @param {HTMLElement} $viewer
 */
function clear_search_highlights($viewer) {
	const $items = $viewer.querySelectorAll(item_$);
	
	$items.forEach($item => {
		$item.classList.remove(
			match_class,
			match_key_class,
			match_value_class,
			match_ancestor_class,
			hidden_by_search_class
		);
		
		// Remove highlight marks from keys
		const $key = $item.querySelector(`.${key_c}`);
		if ($key) {
			const $marks = $key.querySelectorAll('mark');
			$marks.forEach($mark => {
				const text = $mark.textContent;
				$mark.replaceWith(text);
			});
		}
		
		// Remove highlight marks from values
		const $value = $item.querySelector(`.${value_type_string_c}, .${value_type_number_c}, .${value_type_boolean_c}`);
		if ($value) {
			const $marks = $value.querySelectorAll('mark');
			$marks.forEach($mark => {
				const text = $mark.textContent;
				$mark.replaceWith(text);
			});
		}
	});
}


/**
 * Update search count display
 * @param {HTMLElement} $viewer
 * @param {number} count
 */
function update_search_count($viewer, count) {
	const $results = $viewer.querySelector(`.${search_results_c}`);
	if (!$results) return;
	
	const $count = $results.querySelector(`.${search_count_c}`);
	if (!$count) return;
	
	const label = count === 1 ? 'match' : 'matches';
	$count.textContent = `${count} ${label}`;
	$results.hidden = false;
}
